import axios from "axios";
import { REACT_APP_POLICY_API } from "../constants";
import { checkAPISuccess, manageServiceError } from "../utils/helpers";
import { requestsFor } from "./headerService";

const instance = axios.create({ baseURL: REACT_APP_POLICY_API });
const requests = requestsFor(instance);

const path = {
  listPolicy: "/api/v1/policyService/ListPolicy",
  createJSONPolicy: "/api/v1/policyService/CreateJSONPolicy",
  updateJSONPolicy: "/api/v1/policyService/UpdateJSONPolicy",
  policyAction: "/api/v1/policyService/PolicyAction",
  approvePolicyAgentless: "/api/v1/policyService/ApprovePolicyAgentless",
  revokePolicyAgentless: "/api/v1/policyService/RevokePolicyAgentless",
  discoverADPolicies: "/api/v1/policyService/DiscoverADPolicies",
  applyDiscoveredPolicies: "/api/v1/policyService/ApplyDiscoveredPolicies",
  listAD: "/ad/getAllDomains",
  listADGroups: "/api/v1/policy/listADGroups",
  listADUsers: "/api/v1/policy/listADUsers",
  listOU: "/api/v1/policyService/listOU",
  previewImpact: "/api/v1/policy/json/previewImpact",
  effectivePolicy: "/api/v1/policy/json/getEffectivePolicy",
};

export async function listADPolicies({ payload = {}, onSuccess, onFailure }) {
  try {
    const { data: response } = await requests.post(path.listPolicy, payload);
    if (checkAPISuccess(response)) onSuccess && onSuccess(response);
    else onFailure && onFailure(response);
  } catch (error) {
    manageServiceError(error, onFailure, "List AD Policies Failed");
  }
}

// CreateJSONPolicy has three distinct outcomes the caller must tell apart:
//   200 + warning[s]  → created, but the server has something to say (toast it)
//   409 + conflicts[] → NOT created; overlapping policies must be resolved, or
//                       the same payload re-sent with force: true
//   anything else     → plain failure
// onConflict is only called for the 409 case; without it, a 409 falls through
// to onFailure so existing callers keep working.
export async function createADPolicy({ payload = {}, onSuccess, onFailure, onConflict }) {
  try {
    const { data: response } = await requests.post(path.createJSONPolicy, payload);
    if (checkAPISuccess(response)) onSuccess && onSuccess(response);
    else onFailure && onFailure(response);
  } catch (error) {
    const status = error?.response?.status;
    const body = error?.response?.data;
    if (status === 409 && onConflict) {
      onConflict({
        conflicts: body?.conflicts || [],
        warning: body?.warning || body?.message || "",
        raw: body,
      });
      return;
    }
    manageServiceError(error, onFailure, "Create AD Policy Failed");
  }
}

export async function updateADPolicy({ payload = {}, onSuccess, onFailure }) {
  try {
    const { data: response } = await requests.post(path.updateJSONPolicy, payload);
    // UpdateJSONPolicy nests its result: { updatedPolicyID: { code, status, data: "<new policy id>" } }.
    // The backend creates a NEW policy on every update — callers must adopt
    // updatedPolicyID.data as the policy id for any subsequent update.
    if (checkAPISuccess(response) || checkAPISuccess(response?.updatedPolicyID))
      onSuccess && onSuccess(response);
    else onFailure && onFailure(response);
  } catch (error) {
    manageServiceError(error, onFailure, "Update AD Policy Failed");
  }
}

// Approve / Suspend (single or bulk) via the unified PolicyAction endpoint.
export async function policyAction({ payload = {}, onSuccess, onFailure }) {
  try {
    const { data: response } = await requests.post(path.policyAction, payload);
    if (checkAPISuccess(response)) onSuccess && onSuccess(response);
    else onFailure && onFailure(response);
  } catch (error) {
    manageServiceError(error, onFailure, "Policy Action Failed");
  }
}

// Dedicated approval-lifecycle endpoints (agentless variant).
export async function approvePolicyAgentless({ payload = {}, onSuccess, onFailure }) {
  try {
    const { data: response } = await requests.post(path.approvePolicyAgentless, payload);
    if (checkAPISuccess(response)) onSuccess && onSuccess(response);
    else onFailure && onFailure(response);
  } catch (error) {
    manageServiceError(error, onFailure, "Approve Policy Failed");
  }
}

export async function revokePolicyAgentless({ payload = {}, onSuccess, onFailure }) {
  try {
    const { data: response } = await requests.post(path.revokePolicyAgentless, payload);
    if (checkAPISuccess(response)) onSuccess && onSuccess(response);
    else onFailure && onFailure(response);
  } catch (error) {
    manageServiceError(error, onFailure, "Revoke Policy Failed");
  }
}

export async function discoverADPolicies({ payload = {}, onSuccess, onFailure }) {
  try {
    const { data: response } = await requests.post(path.discoverADPolicies, payload);
    if (checkAPISuccess(response)) onSuccess && onSuccess(response);
    else onFailure && onFailure(response);
  } catch (error) {
    manageServiceError(error, onFailure, "Discover AD Policies Failed");
  }
}

export async function applyDiscoveredPolicies({ payload = {}, onSuccess, onFailure }) {
  try {
    const { data: response } = await requests.post(path.applyDiscoveredPolicies, payload);
    if (checkAPISuccess(response)) onSuccess && onSuccess(response);
    else onFailure && onFailure(response);
  } catch (error) {
    manageServiceError(error, onFailure, "Apply Discovered Policies Failed");
  }
}

// { orgId, tenantId, pageNo, pageSize } → { code, domains: [...], totalCount }
// domains[].directoryName is the admin's label; domains[].domainName is the
// FQDN the sensor reports. They are different fields — see domainFqdn().
export async function getAllADDomains({ payload = {}, onSuccess, onFailure }) {
  try {
    const { data: response } = await requests.post(path.listAD, {
      pageNo: 1,
      pageSize: 50,
      ...payload,
    });
    onSuccess && onSuccess(response);
  } catch (error) {
    manageServiceError(error, onFailure, "List AD Domains Failed");
  }
}

// A directory that can't be resolved answers 200 with code: 400 and a message
// in the body ('no active directory matches "x"'). That's a failure with
// something to say — never an empty list.
function pickerFailure(error, fallback) {
  const body = error?.response?.data;
  return {
    message: body?.message || body?.error || fallback,
  };
}

// { org_Id, tenant_Id, adId, filter, page, pageSize }
//   → { code, status, data: [{ id, groupName, memberCount }], total }
// filter is a case-insensitive substring on the group name, applied
// server-side. total is the unpaginated count.
export async function listADGroups({ payload = {}, onSuccess, onFailure }) {
  try {
    const { data: response } = await requests.post(path.listADGroups, payload);
    if (checkAPISuccess(response)) onSuccess && onSuccess(response);
    else
      onFailure &&
        onFailure({ message: response?.message || "Couldn't load AD groups." });
  } catch (error) {
    onFailure && onFailure(pickerFailure(error, "Couldn't load AD groups."));
  }
}

// { org_Id, tenant_Id, adId, filter, page, pageSize }
//   → { code, status, data: [{ id, username, logoname, email }], total }
export async function listADUsers({ payload = {}, onSuccess, onFailure }) {
  try {
    const { data: response } = await requests.post(path.listADUsers, payload);
    if (checkAPISuccess(response)) onSuccess && onSuccess(response);
    else
      onFailure &&
        onFailure({ message: response?.message || "Couldn't load AD users." });
  } catch (error) {
    onFailure && onFailure(pickerFailure(error, "Couldn't load AD users."));
  }
}

export async function listOUs({ payload = {}, onSuccess, onFailure }) {
  try {
    const { data: response } = await requests.post(path.listOU, payload);
    onSuccess && onSuccess(response);
  } catch (error) {
    manageServiceError(error, onFailure, "List OUs Failed");
  }
}

// { orgId, tenantId, domain, adGroups, adUsers, adOus } → { matchedUsers }
// Returns the body verbatim: matchedUsers can legitimately be 0, and the
// endpoint answers 200 without the code field checkAPISuccess looks for.
export async function previewPolicyImpact({ payload = {}, onSuccess, onFailure }) {
  try {
    const { data: response } = await requests.post(path.previewImpact, payload);
    onSuccess && onSuccess(response);
  } catch (error) {
    manageServiceError(error, onFailure, "Preview Policy Impact Failed");
  }
}

// Dry run against the live evaluator — snake_case payload, unlike every other
// policy endpoint here.
// { org_id, tenant_id, ad_user, domain, protocol }
//   → { match_reason, action, mfa_required }
export async function getEffectivePolicy({ payload = {}, onSuccess, onFailure }) {
  try {
    const { data: response } = await requests.post(path.effectivePolicy, payload);
    onSuccess && onSuccess(response);
  } catch (error) {
    manageServiceError(error, onFailure, "Effective Policy Lookup Failed");
  }
}
